// The chrome around a case quote and around the wait that precedes it. One
// auth-based decision serves both, so a signed-in visitor sees the portal
// from the first frame and never a standalone header that swaps out.

import type { ReactNode } from "react";

import CallButton from "components/CallButton";
import Header from "components/Header";
import HeaderPhone from "components/HeaderPhone";
import { useFirebase } from "firebase";
import PortalLayout, { PortalContent } from "layouts/PortalLayout";

import { caseQuoteShell, type CaseQuoteShell } from "./quote-subject";

export function useCaseQuoteShell(): CaseQuoteShell {
  const { user, loading } = useFirebase();
  return caseQuoteShell({ loading, signedIn: user !== null });
}

export function Frame({
  shell,
  line,
  children,
}: {
  shell: CaseQuoteShell;
  /** The number the standalone header offers; the first-call button when absent. */
  line?: { href: string; label: string };
  children: ReactNode;
}) {
  if (shell === "portal") {
    return <PortalLayout>{children}</PortalLayout>;
  }
  if (shell === "pending") {
    return (
      <div className="flex min-h-screen flex-col bg-[#FBF7EC]">{children}</div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col bg-[#FBF7EC]">
      <Header
        quoteFlow
        quoteFlowWidth="form"
        actions={
          line ? <HeaderPhone line={line} /> : <CallButton variant="compact" />
        }
      />
      {children}
    </div>
  );
}

/** A non-quote state (loading, refusal, error) in the portal sits in the
 * portal's content column; the standalone page lays these out itself. */
export function Message({
  shell,
  children,
}: {
  shell: CaseQuoteShell;
  children: ReactNode;
}) {
  if (shell !== "portal") return <>{children}</>;
  return <PortalContent>{children}</PortalContent>;
}
